import { Box, Stack, type StackProps, useTheme } from "@mui/material";
import { AnchoredPopover, Button, FoodsIcon, FoodsIconButton, FoodsTextInput, Typography } from "@foodpilot/foods";
import { useEffect, useId, useMemo, useRef, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { FoodsField, getProductV2SelectFieldStyles } from "./FoodsFields";

type TreeId = number | string;

type TreeNode<T> = {
    option: T;
    children: TreeNode<T>[];
    orphan: boolean;
};

export type FoodsTreeSelectFieldProps<T> = {
    label?: string;
    accessibleLabel?: string;
    required?: boolean;
    helperText?: string;
    errorText?: string;
    selectedOption: T | null;
    options: readonly T[];
    onChange: (option: T | null) => void;
    getId: (option: T) => TreeId;
    getName: (option: T) => string;
    getOrphanName?: (option: T) => string;
    getParentId: (option: T) => TreeId | null;
    getSelectedName?: (option: T) => string;
    isSelectable?: (option: T) => boolean;
    placeholder: string;
    search: string;
    setSearch: (value: string) => void;
    searchPlaceholder: string;
    disableSearch?: boolean;
    emptyOptionsText: string;
    noResultText?: string;
    disabled?: boolean;
    closeOnSelect?: boolean;
    stackProps?: StackProps;
};

const everyOptionIsSelectable = () => true;

const buildTree = <T,>(
    options: readonly T[],
    getId: (option: T) => TreeId,
    getParentId: (option: T) => TreeId | null,
): TreeNode<T>[] => {
    const nodeById = new Map<TreeId, TreeNode<T>>();
    const roots: TreeNode<T>[] = [];

    options.forEach((option) => nodeById.set(getId(option), { option, children: [], orphan: false }));
    options.forEach((option) => {
        const node = nodeById.get(getId(option));
        if (!node) return;

        const parentId = getParentId(option);
        const parent = parentId === null ? undefined : nodeById.get(parentId);
        if (parent && parent !== node) {
            parent.children.push(node);
            return;
        }

        node.orphan = parentId !== null && parent === undefined;
        roots.push(node);
    });

    return roots;
};

const findAncestorIds = <T,>(
    nodes: readonly TreeNode<T>[],
    selectedId: TreeId | null,
    getId: (option: T) => TreeId,
): TreeId[] => {
    if (selectedId === null) return [];

    const visit = (node: TreeNode<T>, ancestors: TreeId[]): TreeId[] | null => {
        if (getId(node.option) === selectedId) return ancestors;

        for (const child of node.children) {
            const result = visit(child, [...ancestors, getId(node.option)]);
            if (result) return result;
        }

        return null;
    };

    for (const root of nodes) {
        const result = visit(root, []);
        if (result) return result;
    }

    return [];
};

const collectParentIds = <T,>(nodes: readonly TreeNode<T>[], getId: (option: T) => TreeId): Set<TreeId> => {
    const ids = new Set<TreeId>();

    const visit = (node: TreeNode<T>) => {
        if (node.children.length === 0) return;

        ids.add(getId(node.option));
        node.children.forEach(visit);
    };
    nodes.forEach(visit);

    return ids;
};

type TreeRowsProps<T> = Pick<
    FoodsTreeSelectFieldProps<T>,
    "getId" | "getName" | "getOrphanName" | "isSelectable" | "selectedOption"
> & {
    nodes: readonly TreeNode<T>[];
    level: number;
    expandedIds: ReadonlySet<TreeId>;
    togglingDisabled: boolean;
    onToggle: (id: TreeId) => void;
    onSelect: (option: T) => void;
};

const TreeRows = <T,>({
    nodes,
    level,
    expandedIds,
    getId,
    getName,
    getOrphanName,
    isSelectable,
    togglingDisabled,
    onToggle,
    onSelect,
    selectedOption,
}: TreeRowsProps<T>) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const selectedId = selectedOption === null ? null : getId(selectedOption);

    return nodes.map((node) => {
        const id = getId(node.option);
        const name = node.orphan && getOrphanName ? getOrphanName(node.option) : getName(node.option);
        const hasChildren = node.children.length > 0;
        const isExpanded = hasChildren && expandedIds.has(id);
        const selectable = isSelectable?.(node.option) ?? true;
        const isSelected = selectable && selectedId === id;
        const activate = () => {
            if (selectable) onSelect(node.option);
            else if (!togglingDisabled) onToggle(id);
        };

        return (
            <Box key={id}>
                <Stack
                    role="treeitem"
                    aria-label={name}
                    aria-level={level + 1}
                    {...(selectable ? { "aria-selected": isSelected } : {})}
                    {...(hasChildren ? { "aria-expanded": isExpanded } : {})}
                    direction="row"
                    alignItems="center"
                    sx={{
                        backgroundColor: isSelected ? theme.palette.primary[100] : "transparent",
                        minHeight: theme.spacing(5),
                        paddingLeft: theme.spacing(level + 1),
                        paddingRight: theme.spacing(1),
                    }}
                >
                    {hasChildren && !togglingDisabled ?
                        <FoodsIconButton
                            size="xs"
                            icon={isExpanded ? "arrowDownShort" : "arrowRightShort"}
                            buttonProps={{
                                "aria-label":
                                    isExpanded ?
                                        t("product.score_details.collapse", "Collapse {{label}}", { label: name })
                                    :   t("product.score_details.expand", "Expand {{label}}", { label: name }),
                                onClick: (event: MouseEvent<HTMLElement>) => {
                                    event.stopPropagation();
                                    onToggle(id);
                                },
                                type: "button",
                            }}
                        />
                    :   <Box aria-hidden sx={{ width: theme.spacing(2.5) }} />}
                    <Button
                        {...(selectable ?
                            {
                                "aria-label": t(
                                    "settings.action_plan_templates.editor.select_action",
                                    "Select {{name}}",
                                    { name },
                                ),
                            }
                        :   { "aria-hidden": true, tabIndex: -1 })}
                        onClick={activate}
                        type="button"
                        variant="text"
                        sx={{ flex: 1, justifyContent: "flex-start", minWidth: 0 }}
                    >
                        <Typography
                            variant={isSelected ? "body-semibold" : "body-regular"}
                            sx={{ color: isSelected ? theme.palette.primary[700] : theme.custom.grey[2800] }}
                        >
                            {name}
                        </Typography>
                    </Button>
                    {isSelected && <FoodsIcon icon="check" size={2} />}
                </Stack>
                {isExpanded && (
                    <Box role="group">
                        <TreeRows
                            nodes={node.children}
                            level={level + 1}
                            expandedIds={expandedIds}
                            getId={getId}
                            getName={getName}
                            getOrphanName={getOrphanName}
                            isSelectable={isSelectable}
                            togglingDisabled={togglingDisabled}
                            onToggle={onToggle}
                            onSelect={onSelect}
                            selectedOption={selectedOption}
                        />
                    </Box>
                )}
            </Box>
        );
    });
};

export const FoodsTreeSelectField = <T,>({
    label,
    accessibleLabel,
    required = false,
    helperText,
    errorText,
    selectedOption,
    options,
    onChange,
    getId,
    getName,
    getOrphanName,
    getParentId,
    getSelectedName = getName,
    isSelectable = everyOptionIsSelectable,
    placeholder,
    search,
    setSearch,
    searchPlaceholder,
    disableSearch = false,
    emptyOptionsText,
    noResultText = emptyOptionsText,
    disabled = false,
    closeOnSelect = true,
    stackProps,
}: FoodsTreeSelectFieldProps<T>) => {
    const theme = useTheme();
    const generatedId = useId();
    const triggerId = `foods-tree-select-trigger-${generatedId}`;
    const labelId = label === undefined ? undefined : `foods-tree-select-label-${generatedId}`;
    const descriptionId = errorText || helperText ? `foods-tree-select-description-${generatedId}` : undefined;
    const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
    const [expandedIds, setExpandedIds] = useState<Set<TreeId>>(() => new Set());
    const nodes = useMemo(() => buildTree(options, getId, getParentId), [getId, getParentId, options]);
    const selectedId = selectedOption === null ? null : getId(selectedOption);
    const selectedAncestorIds = useMemo(() => findAncestorIds(nodes, selectedId, getId), [getId, nodes, selectedId]);

    const lastExpandedSelectedId = useRef<TreeId | null>(null);

    useEffect(() => {
        if (selectedAncestorIds.length === 0 || lastExpandedSelectedId.current === selectedId) return;

        lastExpandedSelectedId.current = selectedId;
        setExpandedIds((current) => {
            if (selectedAncestorIds.every((id) => current.has(id))) return current;
            return new Set([...current, ...selectedAncestorIds]);
        });
    }, [selectedAncestorIds, selectedId]);

    const searching = search.trim() !== "";
    const parentIds = useMemo(() => collectParentIds(nodes, getId), [getId, nodes]);

    const open = anchor !== null;
    const closePopover = () => {
        setAnchor(null);
        setSearch("");
    };
    const displayText = selectedOption === null ? placeholder : getSelectedName(selectedOption);
    const toggleExpanded = (id: TreeId) => {
        setExpandedIds((current) => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const selectOption = (option: T) => {
        onChange(option);
        if (closeOnSelect) closePopover();
    };

    return (
        <FoodsField
            label={label}
            required={required}
            helperText={helperText}
            errorText={errorText}
            labelId={labelId}
            descriptionId={descriptionId}
            announceError
            stackProps={stackProps}
        >
            <Box sx={{ width: "100%", ...getProductV2SelectFieldStyles(theme, selectedOption !== null) }}>
                <Button
                    id={triggerId}
                    aria-labelledby={labelId ? `${labelId} ${triggerId}` : undefined}
                    aria-describedby={descriptionId}
                    aria-invalid={errorText ? true : undefined}
                    aria-expanded={open}
                    aria-haspopup="tree"
                    disabled={disabled}
                    onClick={(event) => (open ? closePopover() : setAnchor(event.currentTarget))}
                    type="button"
                    variant="select"
                    sx={{
                        alignItems: "center",
                        display: "flex",
                        height: theme.spacing(5),
                        justifyContent: "space-between",
                        padding: theme.spacing(0, 1.5),
                        width: "100%",
                    }}
                >
                    <Typography
                        variant="body-medium"
                        sx={{
                            overflow: "hidden",
                            textAlign: "left",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            width: "100%",
                        }}
                    >
                        {displayText}
                    </Typography>
                    <Box
                        aria-hidden
                        sx={{
                            display: "flex",
                            flexShrink: 0,
                            height: theme.spacing(2.5),
                            width: theme.spacing(2.5),
                        }}
                    >
                        <FoodsIcon icon={open ? "arrowUpShort" : "arrowDownShort"} size={2} />
                    </Box>
                </Button>
            </Box>
            <AnchoredPopover
                open={open}
                anchor={anchor}
                onClose={closePopover}
                anchorPosition="rightside"
                variant="headless"
                sx={{ top: theme.spacing(0.625) }}
                paperProps={{
                    border: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.grey[500]}`,
                    borderRadius: theme.spacing(1.5),
                    boxShadow: theme.custom.shadows.grey,
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: theme.spacing(37.5),
                    overflow: "hidden",
                    width: theme.spacing(56.25),
                }}
            >
                <Stack gap={theme.spacing(1)} sx={{ minHeight: 0, padding: theme.spacing(2) }}>
                    {!disableSearch && (
                        <FoodsTextInput
                            value={search}
                            onChange={setSearch}
                            ExtraInputProps={{
                                type: "search",
                                placeholder: searchPlaceholder,
                                inputProps: { "aria-label": searchPlaceholder },
                                endAdornment: <FoodsIcon icon="search" size={2} />,
                            }}
                            StackProps={{ width: "100%" }}
                        />
                    )}
                    {nodes.length === 0 ?
                        <Typography variant="body-regular" sx={{ color: theme.custom.grey[1400] }}>
                            {searching ? noResultText : emptyOptionsText}
                        </Typography>
                    :   <Box
                            role="tree"
                            aria-label={label ?? accessibleLabel ?? placeholder}
                            sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
                        >
                            <TreeRows
                                nodes={nodes}
                                level={0}
                                expandedIds={searching ? parentIds : expandedIds}
                                togglingDisabled={searching}
                                getId={getId}
                                getName={getName}
                                getOrphanName={getOrphanName}
                                isSelectable={isSelectable}
                                onToggle={toggleExpanded}
                                onSelect={selectOption}
                                selectedOption={selectedOption}
                            />
                        </Box>
                    }
                </Stack>
            </AnchoredPopover>
        </FoodsField>
    );
};
